import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { getDealerDrivers } from "services/logistics/getDealerDrivers";
import { DealerDriverDto } from "types/dealerDrivers";
import { toast } from "utils/toast";

type UseDealerDriversParams = {
  dealerId: string;
  shouldLoad: boolean;
};

type DealerDriversCache = {
  dealerId: string;
  drivers: DealerDriverDto[];
};

export function useDealerDrivers({
  dealerId,
  shouldLoad,
}: UseDealerDriversParams) {
  const { t } = useTranslation();
  const cacheRef = useRef<DealerDriversCache | null>(null);
  const [dealerDrivers, setDealerDrivers] = useState<DealerDriverDto[]>();
  const [isLoadingDrivers, setIsLoadingDrivers] = useState(false);

  useEffect(() => {
    if (!dealerId || !shouldLoad) {
      return undefined;
    }

    if (cacheRef.current?.dealerId === dealerId) {
      setDealerDrivers(cacheRef.current.drivers);
      return undefined;
    }

    let cancelled = false;

    const loadDrivers = async () => {
      setIsLoadingDrivers(true);
      try {
        const response = await getDealerDrivers(dealerId);
        const drivers = response?.data ?? [];
        if (cancelled) {
          return;
        }
        cacheRef.current = { dealerId, drivers };
        setDealerDrivers(drivers);
      } catch {
        if (!cancelled) {
          setDealerDrivers(undefined);
          toast(String(t("failedLoadDealerDrivers")), { type: "error" });
        }
      } finally {
        if (!cancelled) {
          setIsLoadingDrivers(false);
        }
      }
    };

    loadDrivers();

    return () => {
      cancelled = true;
    };
  }, [dealerId, shouldLoad, t]);

  useEffect(() => {
    if (!dealerId) {
      cacheRef.current = null;
      setDealerDrivers(undefined);
    }
  }, [dealerId]);

  return {
    dealerDrivers,
    isLoadingDrivers,
  };
}
